import { FC, ReactNode, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  IconButton,
  Typography,
} from "@mui/material";

import { RiCloseLine, RiFilePdf2Line } from "react-icons/ri";
import PaperclipIcon from "../../../../icons/PaperclipIcon";

// Building blocks shared by the claim edit dialogs (Record action, Record remit, Remove payment)

export const FieldError: FC<{ message?: string }> = ({ message }) =>
  message ? (
    <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
      {message}
    </FormHelperText>
  ) : null;

export const Hint: FC<{ children: ReactNode }> = ({ children }) => (
  <FormHelperText sx={{ mx: 0, mt: 0.5 }}>{children}</FormHelperText>
);

/** The field's error when it has one, else its hint. */
export const FieldMessage: FC<{ error?: string; children: ReactNode }> = ({
  error,
  children,
}) => (error ? <FieldError message={error} /> : <Hint>{children}</Hint>);

/** A live checklist of the rules the form must meet, inside a success alert. */
export const RulesList: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <Alert severity="success" sx={{ maxWidth: "100%" }}>
    <Box
      component="ul"
      aria-label={label}
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      {children}
    </Box>
  </Alert>
);

// One live rule in the rules list: darker once the form satisfies it
export const RuleItem: FC<{ met?: boolean; children: ReactNode }> = ({
  met,
  children,
}) => (
  <Box
    component="li"
    data-met={met ? "true" : "false"}
    sx={{
      display: "flex",
      gap: 1,
      alignItems: "flex-start",
      fontSize: "0.8125rem",
    }}
  >
    <Box
      component="span"
      sx={{ color: met ? "text.primary" : "text.secondary" }}
    >
      {children}
    </Box>
  </Box>
);

export const SummaryRow: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <Box sx={{ display: "flex", gap: 2, py: 0.75 }}>
    <Typography
      sx={{
        fontSize: "0.8125rem",
        color: "text.secondary",
        width: 140,
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        fontSize: "0.8125rem",
        fontWeight: 500,
        minWidth: 0,
        wordBreak: "break-word",
        whiteSpace: "pre-wrap",
      }}
    >
      {children}
    </Box>
  </Box>
);

export const NoneText: FC<{ children?: ReactNode }> = ({
  children = "None",
}) => (
  <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
    {children}
  </Box>
);

export const BeforeAfter: FC<{
  before: ReactNode;
  after: ReactNode;
  same?: boolean;
}> = ({ before, after, same = before === after }) =>
  same ? (
    <>{after}</>
  ) : (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap" }}
    >
      <Box
        component="span"
        sx={{
          color: "text.secondary",
          textDecoration: "line-through",
          mr: 0.75,
        }}
      >
        {before}
      </Box>
      → {after}
    </Box>
  );

/** The grey "EFFECT ON …" panel of a confirmation step. */
export const EffectPanel: FC<{ title: string; children: ReactNode }> = ({
  title,
  children,
}) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: "8px",
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "grey.50",
    }}
  >
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontWeight: 700,
        color: "text.secondary",
        letterSpacing: "0.03em",
        mb: 0.5,
        textTransform: "uppercase",
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

/** An API refusal on a confirmation step, with an optional retry. */
export const EditErrorAlert: FC<{
  message: string;
  onRetry?: () => void;
  isRetrying?: boolean;
}> = ({ message, onRetry, isRetrying }) => (
  <Alert
    severity="error"
    sx={{
      maxWidth: "100%",
      alignItems: "center",
      "& .MuiAlert-action": {
        py: 0,
        mr: 0,
        // The theme paints alert-action buttons with the alert's own light
        // background, which makes them invisible; use the alert red instead
        "& .MuiButton-root": {
          bgcolor: "alert_error.main",
          color: "common.white",
          "&:hover": { bgcolor: "error.dark" },
          "&.Mui-disabled": {
            bgcolor: "alert_error.main",
            color: "common.white",
            opacity: 0.7,
          },
        },
      },
    }}
    action={
      onRetry ? (
        <Button
          size="small"
          onClick={onRetry}
          disabled={isRetrying}
          startIcon={
            isRetrying ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
          sx={{ whiteSpace: "nowrap" }}
        >
          Try again
        </Button>
      ) : undefined
    }
  >
    {message}
  </Alert>
);

/**
 * One optional PDF: a drop zone, or the chosen file with a remove button.
 * `onFiles` gets what was picked or dropped, so the caller validates it.
 */
export const PdfDropzone: FC<{
  label: string;
  file: File | null;
  onFiles: (files: File[]) => void;
  onRemove: () => void;
  disabled?: boolean;
  hasError?: boolean;
  inputTestId?: string;
}> = ({ label, file, onFiles, onRemove, disabled, hasError, inputTestId }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <Box>
      <Typography
        variant="body2"
        component="label"
        sx={{
          color: "text.primary",
          fontWeight: 700,
          mb: 0.5,
          fontSize: "0.875rem",
          display: "block",
        }}
      >
        {label}
      </Typography>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        hidden
        data-testid={inputTestId}
        onChange={(e) => {
          if (e.target.files?.length) onFiles(Array.from(e.target.files));
          e.target.value = "";
        }}
      />
      {file ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
          }}
        >
          <Box sx={{ color: "error.main", display: "inline-flex" }}>
            <RiFilePdf2Line size={20} />
          </Box>
          <Typography
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: "0.8125rem",
              fontWeight: 600,
            }}
            title={file.name}
          >
            {file.name}
          </Typography>
          <IconButton
            size="small"
            aria-label="Remove document"
            disabled={disabled}
            onClick={onRemove}
          >
            <RiCloseLine size={16} />
          </IconButton>
        </Box>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          aria-label="Attach a PDF"
          onClick={() => !disabled && fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !disabled)
              fileInputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            if (!disabled && e.dataTransfer.files?.length)
              onFiles(Array.from(e.dataTransfer.files));
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.5,
            border: "2px dashed",
            borderColor: hasError
              ? "error.main"
              : isDragOver
                ? "primary.main"
                : "divider",
            borderRadius: "8px",
            cursor: "pointer",
            color: "text.secondary",
            fontSize: "0.8125rem",
          }}
        >
          <PaperclipIcon />
          Attach a PDF or drop it here
        </Box>
      )}
    </Box>
  );
};
